import type { ApiEndpoints } from '@avisoma/shared';

export type Snapshot = ApiEndpoints<string>['/api/system/pipeline-status']['GET']['response'];
export type Stage = Snapshot['stages'][number];

export const SNAPSHOT_WINDOW_MS = 60 * 60_000;

export const appendSnapshot = (history: Snapshot[], next: Snapshot, windowMs = SNAPSHOT_WINDOW_MS): Snapshot[] => {
  const cutoff = new Date(next.generatedAt).getTime() - windowMs;
  return [...history.filter((s) => new Date(s.generatedAt).getTime() >= cutoff && s.generatedAt !== next.generatedAt), next]
    .sort((a, b) => a.generatedAt.localeCompare(b.generatedAt));
};

export type OverallState = 'stalled' | 'errors' | 'running' | 'idle';

// Stages with unavailable stats intentionally count as idle here — partial
// Cloud Tasks outages surface on the stage node itself, not the overall chip.
export const overallState = (stages: Stage[]): OverallState => {
  if (stages.some((s) => s.status === 'stalled')) return 'stalled';
  if (stages.some((s) => s.recentErrors > 0)) return 'errors';
  if (stages.some((s) => s.status === 'active')) return 'running';
  return 'idle';
};

export const stageTotals = (stages: Stage[]) => ({
  queued: stages.reduce((acc, s) => acc + s.queueDepth, 0),
  ratePerMin: stages.reduce((acc, s) => acc + s.ratePerMin, 0),
  instances: stages.reduce((acc, s) => acc + s.instances, 0),
});

/** Average tasks/min per stage across the buffered window, keyed by stage id. */
export const historicalRatesByStage = (history: Snapshot[]): Map<string, number> => {
  const sums = new Map<string, { total: number; count: number }>();
  for (const snap of history) {
    for (const stage of snap.stages) {
      const entry = sums.get(stage.id) ?? { total: 0, count: 0 };
      entry.total += stage.ratePerMin;
      entry.count += 1;
      sums.set(stage.id, entry);
    }
  }
  return new Map([...sums].map(([id, { total, count }]) => [id, total / count]));
};

/** Minutes to drain a backlog at a given rate. null = unknown (backlog present but no measured throughput). */
export const estimateEtaMinutes = (queued: number, avgRatePerMin: number): number | null => {
  if (queued <= 0) return 0;
  if (avgRatePerMin <= 0) return null;
  return queued / avgRatePerMin;
};

/**
 * Global ETA = the slowest individual stage's own ETA — the pipeline can't
 * finish before every stage's own backlog clears, so summing rates across
 * independent queues (as if one stage's throughput could drain another's
 * queue) understates the true time and can even undercut a single stage's
 * own estimate. This is a lower-bound estimate, not the true end-to-end
 * completion time: downstream stages will keep receiving inflow from
 * upstream stages that haven't finished draining yet. null (unknown)
 * propagates: if any stage has a real backlog but no measured throughput,
 * the true completion time is unknown too.
 */
export const estimateGlobalEtaMinutes = (stages: Stage[], avgRates: Map<string, number>): number | null => {
  let max = 0;
  for (const stage of stages) {
    if (stage.queueDepth <= 0) continue;
    const rate = avgRates.get(stage.id) ?? 0;
    if (rate <= 0) return null;
    max = Math.max(max, stage.queueDepth / rate);
  }
  return max;
};

export const formatEta = (minutes: number | null): string => {
  if (minutes === null) return '—';
  if (minutes === 0) return 'done';
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `~${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `~${h}h ${m}m`;
};

/** Badge color/label per overall pipeline state — shared by the monitoring page and the home strip. */
export const STATE_BADGE = {
  stalled: { color: 'red', label: 'Stalled' },
  errors: { color: 'orange', label: 'Errors' },
  running: { color: 'emerald', label: 'Running' },
  idle: { color: 'zinc', label: 'Idle' },
} as const;
